// The festival banner on the store home — the one seller coupon that is live AND drives a festival
// landing page, shown as a block the shopper can actually see rather than a strip they scan past.
//
// WHY THIS EXISTS ALONGSIDE CouponBar
// -----------------------------------
// CouponBar (same folder) already links to the landing page when there is one. It was shaped as a
// sibling of ShippingBar — 11.5px, py-[9px], an announcement — because at the time the coupon had
// nowhere to go. Now that it does, an 11.5px line is the wrong unit to carry a whole festival edit.
// The strip stays exactly as it is, on both home and PDP; this is a second, larger door to the same
// page, on the home page only.
//
// WHERE THE DATA COMES FROM
// -------------------------
// state.storeDetails is /store/open/details, fetched ONCE app-wide in App.js — the same payload
// CouponBar reads. couponFestivalLabel and couponFestivalTheme were added to it for this banner so
// it can render in the festival's own colours on the first paint, with no second request and no
// spinner. The theme is the scf_theme row from 173-am.sql, handed through as raw JSON: the frontend
// is the only thing that interprets it, which is what keeps "a new festival is a database row"
// true — a festival added by someone who never opens this repo still gets a banner.
//
// VISUAL LANGUAGE
// ---------------
// Deliberately the landing page's own hero, scaled down: same palette, same font-festival display
// face, same dashed coupon chip, same FestivalArt motif. Tapping the banner should feel like
// walking through a door into the same room, not arriving somewhere new. That also means the
// contrast pairs are the page's, already in use on a full-screen hero: eyebrow in `accent` on
// `bg`, headline in `soft`, sub in CREAM, and the CTA inverted (accent ground, bg text).
//
// SIZING
// ------
// Contained, never full-bleed: `mx-4` matches OwnerBand and HomeSearchBar directly above, which are
// `mx-4` on mobile and `lg:mx-0` inside a `lg:px-4` row. Measured at 360/414/768/1280/1440, the
// banner's left and right edges land on exactly the same pixels as those two cards.
//
// Height is a FLOOR (min-h), not a fixed box, plus two-line clamps on the headline and sub as the
// ceiling. The mobile floor (198px) is the measured WORST case -- two-line headline, two-line sub,
// button row -- so within one width the height is the same whatever the copy says (200px banner on
// 360-414 phones, hairline included) and nothing below moves once the store-details payload lands.
// Desktop keeps its own floor (md:min-h-[178px], 195px banner from 1280). Re-measure and move the
// floor if anything inside the text column gets taller, or a long headline will grow the block.
//
// MOBILE BUTTON ROW: "Shop now" and the coupon chip sit on ONE line from 360px up (12 Oct). That is
// what took the phone banner from 245px to 200px. It costs three things, all below md only: a
// smaller button and chip (padding, font, code tracking), a tighter gap, and the validity phrase
// ("till 5 Nov"), which is hidden on phones and shown from md. At 360 the row is 211 of 212px for a
// 10-character code, so a longer code (codes go up to 12) WRAPS the chip onto its own line rather
// than overflowing or truncating -- that is what the row's flex-wrap is still for.

import React, { useMemo } from "react";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import FestivalArt from "../Offers/FestivalArt";
import { formatValidity } from "../../Utils/couponValidity";

// Same body colour the landing page uses for text on the festival ground (FestivalPage's CREAM).
const CREAM = "#E8DFD8";

// Palette fallbacks, identical to FestivalPage's. A theme row missing a colour renders Diwali's
// rather than an unstyled black box.
const FALLBACK = {
  bg: "#2B0B16",
  accent: "#F2B544",
  accent2: "#C2562B",
  soft: "#FAF6F1",
};

/**
 * @param {string} [ownerName] - the store owner's first name, for the theme's "{owner}" slot.
 *   Passed in rather than fetched: MyStore already resolved it from /about_me.
 */
export default function CouponLandingBanner({ ownerName = "" }) {
  const { t } = useTranslation();
  const storeDetails = useSelector((s) => s.storeDetails);

  const slug = storeDetails?.couponLandingPageSlug;
  const code = storeDetails?.couponCode;
  const percent = storeDetails?.couponPercent;
  const rawTheme = storeDetails?.couponFestivalTheme;

  // The server sends scf_theme verbatim, so a hand-edited row with a stray character must not take
  // the store home down with it — an unparseable theme simply means no banner. The strip above is
  // unaffected and still links through.
  const theme = useMemo(() => {
    if (!rawTheme) return null;
    try {
      const parsed = JSON.parse(rawTheme);
      return parsed && typeof parsed === "object" ? parsed : null;
    } catch {
      return null;
    }
  }, [rawTheme]);

  // All four are required. slug is what makes it clickable, theme is what makes it a banner rather
  // than a coloured rectangle, and code/percent are the offer itself. Anything missing → nothing
  // rendered, so a store with no festival coupon shows no block and no gap.
  if (!slug || !theme || !code || !percent) return null;

  const palette = theme.palette || {};
  const bg = palette.bg || FALLBACK.bg;
  const accent = palette.accent || FALLBACK.accent;
  const accent2 = palette.accent2 || FALLBACK.accent2;
  const soft = palette.soft || FALLBACK.soft;

  // Same substitution the landing page does, so the two read as one voice.
  const sub = String(theme.sub || "").replace("{owner}", ownerName);
  // "till 10 Nov" / "today only" / null when the coupon never expires.
  const validity = formatValidity(storeDetails?.couponValidTill);
  // The label is the fallback headline: a festival row always has scf_label, but scf_theme could
  // in principle carry no headline, and "Karwa Chauth" beats an empty line.
  const headline = theme.headline || storeDetails?.couponFestivalLabel || "";

  return (
    <Link
      to={`/${slug}`}
      /* overflow-hidden is load-bearing twice over: it clips the motif, which is deliberately
         wider than its column on desktop, and it guarantees nothing inside can push the page
         sideways at any width. */
      /* FOCUS: the house pattern (focus-visible ring-2, offset-2), drawn OUTSIDE the block, so it
         sits on the page and the 16px side margin leaves it room. Ring colour is the festival's
         own ground (palette.bg): every seeded palette's bg is 11.8:1-18.1:1 against white, while
         the gold accents are only 1.5-2.1:1 and would fail the 3:1 a focus indicator needs. The
         2px white offset separates the ring from the block, which is the same colour. focus-visible,
         not focus: a tap leaves no ring, a keyboard Tab does. */
      className="block mx-4 mb-[16px] rounded-lg overflow-hidden hover:opacity-[0.97] transition-opacity focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
      style={{ background: bg, "--tw-ring-color": bg, "--tw-ring-offset-color": "#FFFFFF" }}
      aria-label={t("offers.banner.aria", { festival: headline })}
    >
      {/* The landing page opens on this same accent hairline. */}
      <div className="h-[2px] opacity-60" style={{ background: accent }} />

      {/* min-h reserves the block's height the moment it renders, so a two-line headline and a
          one-line headline occupy the same space and the sections below never move. */}
      <div className="flex items-center gap-[12px] sm:gap-[20px] md:gap-[28px] px-[16px] py-[14px] md:px-[24px] md:py-[20px] min-h-[198px] md:min-h-[178px]">
        {/* min-w-0 is what actually prevents horizontal overflow: without it a flex child refuses
            to shrink below its longest word and the banner grows past the viewport. */}
        <div className="flex-1 min-w-0 flex flex-col gap-[5px] md:gap-[9px]">
          {/* leading-[1.5] for the same reason as the headline below: `truncate` is
              overflow-hidden, so a tight line box would shave the tail of a descender. The seeded
              eyebrows are all caps, but nothing stops a new festival row from using lowercase. */}
          <div
            className="text-[10.5px] md:text-[12px] leading-[1.5] tracking-[2.2px] font-[500] truncate"
            style={{ color: accent }}
          >
            {theme.eyebrow}
          </div>

          {/* DESCENDERS. A clamp is `-webkit-box` + overflow-hidden, which clips to the line
              BOX, not to the ink: at leading-[1.12] the box ended above the bottom of a "g", so
              "festival of lights" lost the tail of its g. Marcellus (font-festival) has deep
              descenders, and 26-34px makes the loss obvious.
              Two changes, both needed: leading-[1.2] gives the glyph room inside its own line box,
              and pb-[3px] pads the clipped box itself so the LAST line's descender has somewhere
              to go — padding sits outside the line boxes, so it does not count toward the clamp.
              Still two lines, then an ellipsis.

              CLAMPED WITH ARBITRARY PROPERTIES, NOT `line-clamp-N`. App.css defines a bare
              `.line-clamp-2` of its own, outside any @layer, and it is emitted AFTER Tailwind's
              utilities. Same specificity, later in the cascade — so `.line-clamp-2` beats
              `md:line-clamp-1`, and the desktop override on the sub below silently did nothing
              (computed -webkit-line-clamp read 2 at 1280px). Spelling the property out sidesteps
              that collision entirely, because App.css defines no such class. */}
          <h2
            className="m-0 pb-[3px] font-festival font-[400] text-[26px] md:text-[34px] leading-[1.2] tracking-[-0.2px] break-words [display:-webkit-box] [-webkit-box-orient:vertical] [-webkit-line-clamp:2] overflow-hidden"
            style={{ color: soft }}
          >
            {headline}
          </h2>

          {sub && (
            <p
              /* TWO lines on a phone, one from md. The phone's column is roughly half as wide, so
                 one line there cut the sentence mid-clause; a desktop column fits it whole and a
                 second line would only add height. Arbitrary properties for the cascade reason
                 given on the headline — this is the element the collision actually broke.
                 pb-[2px] for the same descender reason as the headline. */
              className="m-0 pb-[2px] text-[12.5px] md:text-[14px] leading-[1.45] break-words [display:-webkit-box] [-webkit-box-orient:vertical] [-webkit-line-clamp:2] md:[-webkit-line-clamp:1] overflow-hidden"
              style={{ color: CREAM }}
            >
              {sub}
            </p>
          )}

          {/* ONE row on phones from 360px (button + chip side by side), via the mobile-only sizes
              below. flex-wrap stays as the safety net: a code longer than ~10 characters on a
              360px phone drops the chip onto its own line instead of overflowing or truncating
              the code. The 198px floor does NOT reserve that second row, so in that case the
              banner is one row taller -- fixed for the store, no jump after load. */}
          <div className="flex flex-wrap items-center gap-[5px] md:gap-[10px] mt-[4px]">
            <span
              className="inline-flex items-center flex-none px-[9px] py-[7px] md:px-[16px] md:py-[9px] rounded-pill text-[11.5px] md:text-[13.5px] leading-[1.45] font-[500]"
              style={{ background: accent, color: bg }}
            >
              {t("offers.banner.cta")}
            </span>

            {/* The offer, in the landing page's own dashed-chip treatment.
                THREE wrappable segments, each nowrap INSIDE itself, rather than one nowrap line.
                A code at the 12-character maximum plus percent plus validity is wider than a
                360px phone's text column, and a single nowrap line spilled past the dashed border
                (the page did not scroll -- the banner clips -- but the chip looked broken). Each
                segment carries its own separator so a wrap never strands a lone "·". */}
            <span
              className="inline-flex flex-wrap items-center gap-x-[3px] md:gap-x-[7px] gap-y-[2px] max-w-full px-[7px] md:px-[12px] py-[6px] md:py-[7px] rounded-[10px] text-[11px] md:text-[12.5px] leading-[1.45]"
              style={{ border: `1.5px dashed ${accent}`, color: soft }}
            >
              <span className="font-[500] tracking-[0.2px] md:tracking-[1.4px] whitespace-nowrap">
                {code}
              </span>
              <span className="font-[500] whitespace-nowrap">
                <span aria-hidden="true" style={{ color: CREAM }}>
                  {"· "}
                </span>
                {t("offers.hero.off", { percent })}
              </span>
              {validity && (
                <span className="hidden md:inline whitespace-nowrap" style={{ color: CREAM }}>
                  <span aria-hidden="true">{"· "}</span>
                  {validity}
                </span>
              )}
            </span>
          </div>
        </div>

        {/* Beside the text at every width, narrow on a phone so the copy keeps the room it needs.
            flex-none: the art never steals width from the text, it only gives it up. */}
        <div className="flex-none w-[72px] sm:w-[132px] md:w-[184px]">
          <FestivalArt
            motif={theme.motif}
            accent={accent}
            accent2={accent2}
            className="w-full h-auto block"
          />
        </div>
      </div>
    </Link>
  );
}
